import React, { useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const COMPARISON_ROWS = [{
  typical: 'One vendor per service',
  typicalFr: 'Un fournisseur par service',
  bciti: 'One modular foundation',
  bcitiFr: 'Une fondation modulaire'
}, {
  typical: 'Anonymous citizens',
  typicalFr: 'Citoyens anonymes',
  bciti: 'Verified identities',
  bcitiFr: 'Identités vérifiées'
}, {
  typical: 'Generic notices',
  typicalFr: 'Avis génériques',
  bciti: 'Location-targeted communications',
  bcitiFr: 'Communications ciblées par localisation'
}, {
  typical: 'Call the desk',
  typicalFr: 'Appeler le comptoir',
  bciti: 'Self-service answers',
  bcitiFr: 'Réponses en libre-service'
}, {
  typical: 'Siloed data',
  typicalFr: 'Données en silos',
  bciti: 'Unified operational view',
  bcitiFr: 'Vue opérationnelle unifiée'
}];
const FLOW_STEPS = [{
  en: 'Identity',
  fr: 'Identité'
}, {
  en: 'Communications',
  fr: 'Communications'
}, {
  en: 'Services',
  fr: 'Services'
}, {
  en: 'Transactions',
  fr: 'Transactions'
}, {
  en: 'ATNA Intelligence',
  fr: 'Intelligence ATNA'
}];
const MODULES = [{
  name: 'Identity',
  nameFr: 'Identité',
  to: '/core-infrastructure'
}, {
  name: 'Digital Card',
  nameFr: 'Carte numérique',
  to: '/digital-card'
}, {
  name: 'Requests',
  nameFr: 'Requêtes',
  to: '/requests'
}, {
  name: 'Communications',
  nameFr: 'Communications',
  to: '/communications'
}, {
  name: 'Collect',
  nameFr: 'Collecte',
  to: '/collect'
}, {
  name: 'Smart Water',
  nameFr: 'Compteurs intelligents',
  to: '/smart-water'
}, {
  name: 'Analytics',
  nameFr: 'Analytique',
  to: '/analytics'
}, {
  name: 'ATNA Intelligence',
  nameFr: 'Intelligence ATNA',
  to: '/atna'
}];
const COMPLIANCE = [{
  en: 'Law 25',
  fr: 'Loi 25'
}, {
  en: 'Canadian Hosted',
  fr: 'Hébergé au Canada'
}, {
  en: 'Auditable Actions',
  fr: 'Actions auditables'
}, {
  en: 'Consent Management',
  fr: 'Gestion du consentement'
}];
export function CitiesGovernment() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  useEffect(() => {
    track('Page Viewed', {
      page: 'Cities & Government'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-1334">
      <Header data-id="element-1335" />
      <main className="pt-[120px]" data-id="element-1336">
        {/* SECTION 1: HERO */}
        <section className="pt-24 pb-32 px-6" data-id="element-1337">
          <div className="max-w-7xl mx-auto" data-id="element-1338">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1339">
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.8
            }} data-id="element-1340">
                <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-8" data-id="element-1341">
                  {isFR ? 'SECTEURS · VILLES & GOUVERNEMENT' : 'INDUSTRIES · CITIES & GOVERNMENT'}
                </div>
                <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-tight mb-8" data-id="element-1342">
                  {isFR ? <>
                      Les villes n'ont pas besoin de plus de logiciels.
                      <br data-id="element-1343" />
                      Elles ont besoin d'une fondation.
                    </> : <>
                      Cities don't need more software.
                      <br data-id="element-1344" />
                      They need a foundation.
                    </>}
                </h1>
                <p className="font-sans text-xl text-[#121212]/70 leading-relaxed mb-10" data-id="element-1345">
                  {isFR ? 'Identité vérifiée, communications, services et intelligence — sur une seule fondation. En production municipale depuis 2017.' : 'Verified identity, communications, services and intelligence — on one foundation. In municipal production since 2017.'}
                </p>
                <div className="flex flex-wrap items-center gap-4" data-id="element-1346">
                  <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                  location: 'Cities Hero'
                })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-1347">
                    {isFR ? 'Demander une démo' : 'Request a Demo'}
                    <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-1348" />
                  </Link>
                  <Link to="/brossard" onClick={() => track('Secondary CTA Clicked', {
                  location: 'Cities Hero'
                })} className="inline-flex items-center gap-2 px-8 py-4 border border-[#E4E6EC] text-[#121212] text-base font-medium rounded-none hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-1349">
                    {isFR ? 'Explorer Brossard' : 'Explore Brossard'}
                    <ArrowRightIcon className="w-4 h-4" data-id="element-1350" />
                  </Link>
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} className="flex justify-center" aria-hidden="true" data-id="element-1351">
                <img src={isFR ? 'https://cdn.magicpatterns.com/uploads/8Hd4qGcfVFJBSoD1333yXy/ChatGPT_Image_7_juil._2026,_15_26_12.png' : 'https://cdn.magicpatterns.com/uploads/a2ci2PqK9k4hkmFdi2sFKX/ChatGPT_Image_7_juil._2026,_15_24_20.png'} alt={isFR ? 'Bâtiments municipaux reposant sur une fondation vérifiée — identité, communications, services et intelligence' : 'Municipal buildings resting on One Verified Foundation — identity, communications, services and intelligence'} className="w-full max-w-md h-auto object-contain" data-id="element-1352" />
              </motion.div>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.35
          }} className="mt-20 pt-8 border-t border-[#E4E6EC]" data-id="element-1353">
              <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 font-sans text-sm font-semibold tracking-wide text-[#121212]/55" data-id="element-1354">
                <span data-id="element-1355">
                  {isFR ? '400K+ identités vérifiées' : '400K+ Verified Identities'}
                </span>
                <span className="hidden sm:inline text-[#E4E6EC]" data-id="element-1356">|</span>
                <span data-id="element-1357">{isFR ? '20+ municipalités' : '20+ Communities'}</span>
                <span className="hidden sm:inline text-[#E4E6EC]" data-id="element-1358">|</span>
                <span data-id="element-1359">{isFR ? 'Depuis 2017' : 'Since 2017'}</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: CONTRAST */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1360">
          <div className="max-w-4xl mx-auto" data-id="element-1361">
            <div className="text-center mb-20" data-id="element-1362">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-6 leading-tight" data-id="element-1363">
                {isFR ? <>
                    Des outils déconnectés.
                    <br data-id="element-1364" />
                    Ou une fondation.
                  </> : <>
                    Disconnected tools.
                    <br data-id="element-1365" />
                    Or a foundation.
                  </>}
              </motion.h2>
              <motion.p {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="font-sans text-xl text-[#121212]/60 max-w-2xl mx-auto" data-id="element-1366">
                {isFR ? <>
                    La plupart des villes empilent des logiciels.
                    <br data-id="element-1367" />
                    BCITI part de l'identité.
                  </> : <>
                    Most cities stack software.
                    <br data-id="element-1368" />
                    BCITI starts from identity.
                  </>}
              </motion.p>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="bg-white border border-[#E4E6EC]" data-id="element-1369">
              <div className="grid grid-cols-2 border-b border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1370">
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase border-r border-[#E4E6EC]" data-id="element-1371">
                  {isFR ? 'VILLE TYPIQUE' : 'TYPICAL CITY'}
                </div>
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-[#4F46E5] uppercase" data-id="element-1372">
                  BCITI
                </div>
              </div>
              {COMPARISON_ROWS.map((row, i) => <div key={i} className="grid grid-cols-2 border-b border-[#E4E6EC] last:border-0 hover:bg-[#FAFAFB]/50 transition-colors" data-id="element-1373">
                  <div className="p-6 font-serif text-lg text-[#121212]/50 border-r border-[#E4E6EC]" data-id="element-1374">
                    {isFR ? row.typicalFr : row.typical}
                  </div>
                  <div className="p-6 font-serif text-lg text-[#121212]" data-id="element-1375">
                    {isFR ? row.bcitiFr : row.bciti}
                  </div>
                </div>)}
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="mt-16 text-center" data-id="element-1376">
              <p className="font-serif text-2xl md:text-3xl text-[#121212] leading-snug" data-id="element-1377">
                {isFR ? <>
                    L'identité n'est pas un système de plus.
                    <br data-id="element-1378" />
                    C'est la fondation.
                  </> : <>
                    Identity is not another system.
                    <br data-id="element-1379" />
                    It is the foundation.
                  </>}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 3: COMPOUNDING VALUE */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-1380">
          <div className="max-w-5xl mx-auto text-center" data-id="element-1381">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-10 leading-tight" data-id="element-1382">
              {isFR ? <>
                  Déployez une fois.
                  <br data-id="element-1383" />
                  Étendez sans cesse.
                </> : <>
                  Land once.
                  <br data-id="element-1384" />
                  Expand forever.
                </>}
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-xl text-[#121212]/70 leading-relaxed max-w-2xl mx-auto mb-20 space-y-1" data-id="element-1385">
              {isFR ? <>
                  <p data-id="element-1386">Les villes déploient d'abord l'identité vérifiée.</p>
                  <p data-id="element-1387">Puis les communications.</p>
                  <p data-id="element-1388">Puis les requêtes.</p>
                  <p data-id="element-1389">Puis la collecte.</p>
                  <p data-id="element-1390">Puis les compteurs intelligents.</p>
                  <p data-id="element-1391">Puis l'intelligence.</p>
                  <p className="text-[#121212] pt-4" data-id="element-1392">
                    Chaque module s'appuie sur la même fondation.
                  </p>
                </> : <>
                  <p data-id="element-1393">Cities deploy verified identity first.</p>
                  <p data-id="element-1394">Then communications.</p>
                  <p data-id="element-1395">Then requests.</p>
                  <p data-id="element-1396">Then collection.</p>
                  <p data-id="element-1397">Then smart water.</p>
                  <p data-id="element-1398">Then intelligence.</p>
                  <p className="text-[#121212] pt-4" data-id="element-1399">
                    Every module builds on the same foundation.
                  </p>
                </>}
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="flex flex-wrap items-center justify-center gap-4 mb-20" data-id="element-1400">
              {FLOW_STEPS.map((step, index) => <Fragment key={step.en} data-id="element-1401">
                  <div className={`px-6 py-4 border ${index === FLOW_STEPS.length - 1 ? 'border-[#4F46E5] bg-[#4F46E5]/5 text-[#4F46E5]' : 'border-[#E4E6EC] bg-white text-[#121212]'} font-sans text-sm font-medium`} data-id="element-1402">
                    {isFR ? step.fr : step.en}
                  </div>
                  {index < FLOW_STEPS.length - 1 && <ArrowRightIcon className="w-4 h-4 text-[#E4E6EC] hidden md:block" data-id="element-1403" />}
                </Fragment>)}
            </motion.div>

            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="font-serif text-2xl md:text-3xl text-[#121212] leading-snug" data-id="element-1404">
              {isFR ? <>
                  Pas un coût récurrent.
                  <br data-id="element-1405" />
                  Un actif cumulatif.
                </> : <>
                  Not a recurring cost.
                  <br data-id="element-1406" />A compounding asset.
                </>}
            </motion.p>
          </div>
        </section>

        {/* SECTION 4: MUNICIPAL PROOF */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1407">
          <div className="max-w-6xl mx-auto" data-id="element-1408">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-20" data-id="element-1409">
              {isFR ? 'Éprouvé en production municipale.' : 'Proven in municipal production.'}
            </motion.h2>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} data-id="element-1410">
              <div className="font-sans text-xs font-semibold tracking-widest text-[#4F46E5] uppercase mb-6" data-id="element-1411">
                {isFR ? 'Déploiements BCITI' : 'BCITI Deployments'}
              </div>

              <div className="space-y-6" data-id="element-1412">
                <div className="bg-white border border-[#E4E6EC] p-8" data-id="element-1413">
                  <h3 className="font-serif text-2xl text-[#121212] mb-4" data-id="element-1414">
                    Brossard
                  </h3>
                  <div className="flex flex-wrap gap-x-6 gap-y-2 font-sans text-sm text-[#121212]/70" data-id="element-1415">
                    <span data-id="element-1416">
                      {isFR ? '88 266 identités vérifiées' : '88,266 Verified Identities'}
                    </span>
                    <span className="hidden sm:inline text-[#E4E6EC]" data-id="element-1417">|</span>
                    <span data-id="element-1418">
                      {isFR ? '78 % de couverture de la population' : '78% Population Coverage'}
                    </span>
                    <span className="hidden sm:inline text-[#E4E6EC]" data-id="element-1419">|</span>
                    <span data-id="element-1420">{isFR ? 'Depuis 2017' : 'Since 2017'}</span>
                  </div>
                </div>

                <div className="bg-white border border-[#E4E6EC] p-8" data-id="element-1421">
                  <h3 className="font-serif text-2xl text-[#121212] mb-4" data-id="element-1422">
                    Casselman
                  </h3>
                  <div className="font-sans text-sm text-[#121212]/70" data-id="element-1423">
                    {isFR ? 'Compteurs intelligents en production' : 'Smart Water in Production'}
                  </div>
                </div>

                <div className="bg-white border border-[#E4E6EC] p-8" data-id="element-1424">
                  <h3 className="font-serif text-2xl text-[#121212] mb-4" data-id="element-1425">
                    Clarence-Rockland
                  </h3>
                  <div className="flex flex-wrap gap-x-6 gap-y-2 font-sans text-sm text-[#121212]/70" data-id="element-1426">
                    <span data-id="element-1427">
                      {isFR ? 'Collecte avec ATNA' : 'Collect with ATNA'}
                    </span>
                    <span className="hidden sm:inline text-[#E4E6EC]" data-id="element-1428">|</span>
                    <span data-id="element-1429">
                      {isFR ? 'Premier déploiement IA' : 'First AI Deployment'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-white border border-[#E4E6EC] px-8 py-6 text-center" data-id="element-1430">
                <p className="font-serif text-xl md:text-2xl text-[#121212] leading-snug" data-id="element-1431">
                  <span className="text-[#121212]/40" data-id="element-1432">
                    {isFR ? 'Un réseau de capteurs mesure.' : 'A sensor network measures.'}
                  </span>{' '}
                  {isFR ? "L'identité vérifiée agit." : 'Verified identity acts.'}
                </p>
              </div>
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="mt-12 border-t border-[#E4E6EC] pt-12" data-id="element-1433">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1434">
                <div className="border border-[#E4E6EC] bg-white p-8 flex flex-col" data-id="element-1435">
                  <div className="font-sans text-xs font-semibold tracking-[0.18em] text-[#4F46E5] uppercase mb-6" data-id="element-1436">
                    {isFR ? 'La recherche montre' : 'Research Shows'}
                  </div>
                  <ul className="space-y-5 mb-8" data-id="element-1437">
                    <li className="font-sans text-base text-[#121212]/70 leading-relaxed" data-id="element-1438">
                      {isFR ? 'Les résidents préfèrent les canaux numériques au téléphone ou au présentiel dans un rapport de deux pour un.' : 'Residents prefer digital channels over phone or in-person by two to one.'}
                    </li>
                    <li className="font-sans text-base text-[#121212]/70 leading-relaxed" data-id="element-1439">
                      {isFR ? "Améliorer la satisfaction pourrait réduire les appels de service jusqu'à 20 % pour certains services." : 'Improving satisfaction could cut service calls by up to 20% for some services.'}
                    </li>
                    <li className="font-sans text-base text-[#121212]/70 leading-relaxed" data-id="element-1440">
                      {isFR ? 'La confiance peut presque doubler — de 37 % à 72 % — à mesure que la qualité du service augmente.' : 'Trust can nearly double — from 37% to 72% — as service quality rises.'}
                    </li>
                    <li className="font-sans text-base text-[#121212]/70 leading-relaxed" data-id="element-1441">
                      {isFR ? 'Les résidents satisfaits sont 9× plus susceptibles de faire confiance à leur gouvernement.' : 'Satisfied residents are 9× more likely to trust their government.'}
                    </li>
                  </ul>
                  <div className="mt-auto border-t border-[#E4E6EC] pt-6 font-sans text-xs text-[#121212]/40 leading-relaxed" data-id="element-1442">
                    {isFR ? 'McKinsey & Company, recherche sur le secteur public. Contexte de marché ; pas des résultats de déploiement BCITI.' : 'McKinsey & Company, public-sector research. Market context; not BCITI deployment results.'}
                  </div>
                </div>

                <div className="border border-[#E4E6EC] bg-white p-8 flex flex-col" data-id="element-1443">
                  <div className="font-sans text-xs font-semibold tracking-[0.18em] text-[#4F46E5] uppercase mb-6" data-id="element-1444">
                    {isFR ? 'Contexte du marché canadien' : 'Canadian Market Context'}
                  </div>
                  <p className="font-sans text-base text-[#121212]/70 leading-relaxed mb-8" data-id="element-1445">
                    {isFR ? 'Selon le ' : 'Per the '}
                    <a href="https://www.mnp.ca/en/clients/public-sector/2025-municipal-report" target="_blank" rel="noopener noreferrer" className="text-[#4F46E5] underline underline-offset-2 hover:text-[#4F46E5]/80 transition-colors" data-id="element-1446">
                      {isFR ? 'Rapport municipal MNP 2025' : 'MNP 2025 Municipal Report'}
                    </a>
                    {isFR ? ', 23 % des municipalités canadiennes utilisent désormais l’IA et l’IA est une priorité pour 48 % — mais les systèmes hérités en freinent 43 %. L’appétit est réel ; la fondation manque.' : ', 23% of Canadian municipalities now use AI and AI is a priority for 48% — yet legacy systems hold back 43% of them. The appetite is real; the foundation is missing.'}
                  </p>
                  <div className="mt-auto border-t border-[#E4E6EC] pt-6 font-sans text-xs text-[#121212]/40 leading-relaxed" data-id="element-1447">
                    {isFR ? 'Contexte de marché. Pas des résultats de déploiement BCITI. Source : Rapport municipal MNP 2025.' : 'Market context. Not BCITI deployment results. Source: MNP 2025 Municipal Report.'}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 5: WHAT A CITY DEPLOYS */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-1448">
          <div className="max-w-5xl mx-auto" data-id="element-1449">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-16" data-id="element-1450">
              {isFR ? "Ce qu'une ville déploie." : 'What a city deploys.'}
            </motion.h2>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-id="element-1451">
              {MODULES.map(mod => <Link key={mod.name} to={mod.to} className="group flex items-center justify-between p-6 border border-[#E4E6EC] bg-white hover:border-[#4F46E5] transition-colors" data-id="element-1452">
                  <span className="font-sans text-sm font-medium text-[#121212] group-hover:text-[#4F46E5] transition-colors" data-id="element-1453">
                    {isFR ? mod.nameFr : mod.name}
                  </span>
                  <ArrowRightIcon className="w-4 h-4 text-[#121212]/20 group-hover:text-[#4F46E5] transition-colors" data-id="element-1454" />
                </Link>)}
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3" data-id="element-1455">
              {COMPLIANCE.map((item, i, arr) => <Fragment key={item.en} data-id="element-1456">
                  <span className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase" data-id="element-1457">
                    {isFR ? item.fr : item.en}
                  </span>
                  {i < arr.length - 1 && <span className="hidden sm:inline text-[#E4E6EC]" data-id="element-1458">|</span>}
                </Fragment>)}
            </motion.div>
          </div>
        </section>

        {/* SECTION 6: CLOSING */}
        <section className="py-40 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1459">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1460">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-8 leading-tight" data-id="element-1461">
              {isFR ? "Une fondation municipale bâtie aujourd'hui devient difficile à remplacer." : 'A municipal foundation built today becomes difficult to replace.'}
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-xl text-[#121212]/60 mb-12 leading-relaxed space-y-2" data-id="element-1462">
              {isFR ? <>
                  <p data-id="element-1463">Neuf ans en production.</p>
                  <p data-id="element-1464">Des centaines de milliers d'identités vérifiées.</p>
                  <p data-id="element-1465">
                    Les villes qui bâtissent sur une fondation ne reviennent pas
                    aux outils déconnectés.
                  </p>
                </> : <>
                  <p data-id="element-1466">Nine years in production.</p>
                  <p data-id="element-1467">Hundreds of thousands of verified identities.</p>
                  <p data-id="element-1468">
                    Cities that build on a foundation do not return to
                    disconnected tools.
                  </p>
                </>}
            </motion.div>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="flex flex-wrap items-center justify-center gap-4" data-id="element-1469">
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Cities Final'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-1470">
                {isFR ? 'Demander une démo' : 'Request a Demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-1471" />
              </Link>
              <Link to="/brossard" onClick={() => track('Secondary CTA Clicked', {
              location: 'Cities Final'
            })} className="inline-flex items-center gap-2 px-8 py-4 border border-[#E4E6EC] text-[#121212] text-base font-medium rounded-none hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-1472">
                {isFR ? 'Explorer Brossard' : 'Explore Brossard'}
                <ArrowRightIcon className="w-4 h-4" data-id="element-1473" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer data-id="element-1474" />
    </div>;
}